import React, { useState, useEffect } from 'react';
import { 
  Play,
  Pause,
  RotateCcw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  BarChart3,
  Eye,
  GitCompare,
  Package,
  RefreshCw,
  Download,
  Upload,
  Zap,
  Shield,
  Activity
} from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Badge } from '../ui/Table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const EnhancedStripeSync = () => {
  const [activeTab, setActiveTab] = useState('sync');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // State for different tabs
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncBatches, setSyncBatches] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [reconciliationReports, setReconciliationReports] = useState([]);
  const [dryRunResults, setDryRunResults] = useState(null);
  
  // UI state
  const [selectedObjects, setSelectedObjects] = useState(new Set());
  const [showDryRunModal, setShowDryRunModal] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [syncInProgress, setSyncInProgress] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (activeTab === 'sync') {
        await fetchSyncStatus();
      } else if (activeTab === 'batches') {
        await fetchBatchSyncs();
      } else if (activeTab === 'conflicts') {
        await fetchConflicts();
      } else if (activeTab === 'reconciliation') {
        await fetchReconciliationReports();
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncStatus = async () => {
    const response = await fetch(`${BACKEND_URL}/api/admin/monetization/stripe-sync`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      setSyncStatus(data);
    } else {
      throw new Error('Failed to fetch sync status');
    }
  };

  const fetchBatchSyncs = async () => {
    const response = await fetch(`${BACKEND_URL}/api/admin/stripe-sync/batches`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      setSyncBatches(data.batches || []);
    } else {
      throw new Error('Failed to fetch batch syncs');
    }
  };

  const fetchConflicts = async () => {
    // For now, simulate conflicts since we don't have real Stripe data
    setConflicts([]);
  };

  const fetchReconciliationReports = async () => {
    const response = await fetch(`${BACKEND_URL}/api/admin/stripe-sync/reconciliation`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      setReconciliationReports(data.reports || []);
    } else {
      throw new Error('Failed to fetch reconciliation reports');
    }
  };

  const handleDryRunSync = async () => {
    if (selectedObjects.size === 0) {
      setError('Please select objects to sync');
      return;
    }

    setSyncInProgress(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/stripe-sync/dry-run`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          object_ids: Array.from(selectedObjects),
          object_type: 'plan',
          sync_direction: 'to_stripe'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setDryRunResults(data);
        setShowDryRunModal(true);
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to perform dry-run');
      }
    } catch (err) {
      setError('Failed to perform dry-run');
    } finally {
      setSyncInProgress(false);
    }
  };

  const handleBatchSync = async (dryRun = false) => {
    if (selectedObjects.size === 0) {
      setError('Please select objects to sync');
      return;
    }

    setSyncInProgress(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/stripe-sync/batch`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: `Batch sync ${new Date().toISOString().slice(0, 16)}`,
          object_ids: Array.from(selectedObjects),
          object_type: 'plan',
          sync_direction: 'to_stripe',
          dry_run: dryRun
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (activeTab === 'batches') {
          fetchBatchSyncs();
        }
        setSelectedObjects(new Set());
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to execute batch sync');
      }
    } catch (err) {
      setError('Failed to execute batch sync');
    } finally {
      setSyncInProgress(false);
    }
  };

  const handleRollback = async (batchId, selectiveIds = null) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/stripe-sync/batches/${batchId}/rollback`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          object_ids: selectiveIds
        })
      });

      if (response.ok) {
        fetchBatchSyncs();
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to rollback batch');
      }
    } catch (err) {
      setError('Failed to rollback batch');
    }
  };

  const handleManualReconciliation = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/stripe-sync/reconciliation/trigger`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ scope: 'all' })
      });

      if (response.ok) {
        fetchReconciliationReports();
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to trigger reconciliation');
      }
    } catch (err) {
      setError('Failed to trigger reconciliation');
    }
  };

  const SyncTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Stripe Synchronization</h3>
          <p className="text-gray-600 dark:text-gray-300">Manage bidirectional sync with conflict resolution</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleDryRunSync}
            disabled={selectedObjects.size === 0 || syncInProgress}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <Eye className="w-4 h-4 mr-2 inline" />
            Dry Run Preview
          </button>
          <button 
            onClick={() => handleBatchSync(false)}
            disabled={selectedObjects.size === 0 || syncInProgress}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {syncInProgress ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline"></div>
                Syncing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2 inline" />
                Sync Selected
              </>
            )}
          </button>
        </div>
      </div>

      {/* Sync Statistics */}
      {syncStatus && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Objects', value: syncStatus.stats?.total || 0, icon: Package, color: 'text-blue-600' },
            { label: 'Synced', value: syncStatus.stats?.synced || 0, icon: CheckCircle, color: 'text-green-600' },
            { label: 'Pending', value: syncStatus.stats?.pending || 0, icon: Clock, color: 'text-yellow-600' },
            { label: 'Conflicts', value: syncStatus.stats?.conflicts || 0, icon: AlertTriangle, color: 'text-red-600' }
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
              <div className="flex items-center">
                <stat.icon className={`w-5 h-5 ${stat.color} mr-3`} />
                <div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stat.value}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Objects Table with Multi-select */}
      {syncStatus && (
        <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h4 className="text-md font-medium text-gray-900 dark:text-gray-100">Sync Objects</h4>
              <div className="text-sm text-gray-500">
                Selected: {selectedObjects.size} objects
              </div>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedObjects(new Set(syncStatus.records.map(r => r.object_id)));
                      } else {
                        setSelectedObjects(new Set());
                      }
                    }}
                    className="rounded border-gray-300"
                  />
                </TableHead>
                <TableHead>Object</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Sync</TableHead>
                <TableHead>Stripe ID</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {syncStatus.records?.map(record => (
                <TableRow key={record.id}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedObjects.has(record.object_id)}
                      onChange={(e) => {
                        const newSelected = new Set(selectedObjects);
                        if (e.target.checked) {
                          newSelected.add(record.object_id);
                        } else {
                          newSelected.delete(record.object_id);
                        }
                        setSelectedObjects(newSelected);
                      }}
                      className="rounded border-gray-300"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{record.object_id.slice(0, 8)}...</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="info">{record.object_type}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={
                      record.sync_status === 'synced' ? 'success' :
                      record.sync_status === 'error' ? 'error' :
                      record.sync_status === 'conflict' ? 'warning' : 'secondary'
                    }>
                      {record.sync_status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {record.last_successful_sync ? 
                      new Date(record.last_successful_sync).toLocaleDateString() :
                      'Never'
                    }
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs">
                      {record.stripe_id ? record.stripe_id.slice(0, 12) + '...' : 'Not synced'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <button className="p-1 text-gray-500 hover:text-blue-600">
                        <Eye className="w-4 h-4" />
                      </button>
                      {record.sync_status === 'conflict' && (
                        <button 
                          onClick={() => {
                            setSelectedConflict(record);
                            setShowConflictModal(true);
                          }}
                          className="p-1 text-gray-500 hover:text-orange-600"
                        >
                          <GitCompare className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );

  const BatchesTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Batch Operations</h3>
          <p className="text-gray-600 dark:text-gray-300">Track batch sync operations and rollbacks</p>
        </div>
      </div>

      {/* Batches Table */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Batch Name</TableHead>
              <TableHead>Objects</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead>Success Rate</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {syncBatches.map(batch => (
              <TableRow key={batch.id}>
                <TableCell>
                  <div className="font-medium">{batch.name}</div>
                  <div className="text-sm text-gray-500">
                    Created {new Date(batch.created_at).toLocaleDateString()}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div>{batch.total_objects} total</div>
                    <div className="text-gray-500">{batch.object_type}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${batch.progress_percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600">
                      {batch.progress_percentage.toFixed(1)}%
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {batch.processed_count > 0 ? (
                    <div className="text-sm">
                      <div className="text-green-600">{batch.success_count} success</div>
                      {batch.error_count > 0 && (
                        <div className="text-red-600">{batch.error_count} errors</div>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-500">Not started</span>
                  )}
                </TableCell>
                <TableCell>
                  {batch.actual_duration ? 
                    `${batch.actual_duration}s` :
                    batch.estimated_duration ? `~${batch.estimated_duration}s` : 'Unknown'
                  }
                </TableCell>
                <TableCell>
                  <Badge variant={
                    batch.status === 'completed' ? 'success' :
                    batch.status === 'failed' ? 'error' :
                    batch.status === 'running' ? 'warning' : 'secondary'
                  }>
                    {batch.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    {batch.can_rollback && batch.status === 'completed' && (
                      <>
                        <button 
                          onClick={() => handleRollback(batch.id)}
                          className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                        >
                          <RotateCcw className="w-3 h-3 mr-1 inline" />
                          Full Rollback
                        </button>
                        <button 
                          onClick={() => handleRollback(batch.id, [batch.object_ids[0]])}
                          className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                        >
                          Selective
                        </button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const ReconciliationTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Reconciliation Reports</h3>
          <p className="text-gray-600 dark:text-gray-300">Daily reconciliation and discrepancy tracking</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleManualReconciliation}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            <Activity className="w-4 h-4 mr-2 inline" />
            Run Now
          </button>
        </div>
      </div>

      {/* Reconciliation Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {reconciliationReports.length > 0 && (
          (() => {
            const latestReport = reconciliationReports[0];
            return [
              { 
                label: 'Accuracy Rate', 
                value: `${latestReport.accuracy_percentage.toFixed(2)}%`, 
                target: '> 99.9%',
                icon: Shield, 
                color: latestReport.accuracy_percentage > 99.9 ? 'text-green-600' : 'text-red-600' 
              },
              { 
                label: 'Discrepancies', 
                value: latestReport.discrepancies.length, 
                target: 'Minimize',
                icon: AlertTriangle, 
                color: latestReport.discrepancies.length === 0 ? 'text-green-600' : 'text-yellow-600' 
              },
              { 
                label: 'Last Report', 
                value: new Date(latestReport.generated_at).toLocaleDateString(), 
                target: 'Daily at midnight',
                icon: Clock, 
                color: 'text-blue-600' 
              }
            ];
          })()
        ).map((metric, index) => (
          <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-2xl font-bold ${metric.color}`}>{metric.value}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{metric.label}</div>
                <div className="text-xs text-gray-400">{metric.target}</div>
              </div>
              <metric.icon className={`w-6 h-6 ${metric.color}`} />
            </div>
          </div>
        ))}
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Accuracy</TableHead>
              <TableHead>Matched</TableHead>
              <TableHead>Discrepancies</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reconciliationReports.map(report => (
              <TableRow key={report.id}>
                <TableCell>{new Date(report.generated_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Badge variant="info">{report.scope}</Badge>
                </TableCell>
                <TableCell>
                  <span className={report.accuracy_percentage > 99.9 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                    {report.accuracy_percentage.toFixed(2)}%
                  </span>
                </TableCell>
                <TableCell>{report.matched_objects}</TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div className="text-red-600">{report.mismatched_objects} mismatched</div>
                    <div className="text-yellow-600">{report.local_only_objects + report.stripe_only_objects} orphaned</div>
                  </div>
                </TableCell>
                <TableCell>
                  <button className="p-1 text-gray-500 hover:text-blue-600">
                    <Download className="w-4 h-4" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  // Dry Run Preview Modal
  const DryRunModal = () => (
    showDryRunModal && dryRunResults && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Dry Run Preview
          </h3>
          
          {/* Summary */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {Object.entries(dryRunResults.actions_summary || {}).map(([action, count]) => (
              <div key={action} className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{count}</div>
                <div className="text-sm text-gray-500 capitalize">{action}</div>
              </div>
            ))}
          </div>
          
          {/* Detailed Changes */}
          <div className="space-y-4 mb-6">
            {dryRunResults.previews?.map((preview, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium">{preview.object_id.slice(0, 12)}...</div>
                  <Badge variant={
                    preview.action === 'create' ? 'info' :
                    preview.action === 'update' ? 'warning' :
                    preview.action === 'no_change' ? 'success' : 'error'
                  }>
                    {preview.action}
                  </Badge>
                </div>
                
                {preview.differences.length > 0 && (
                  <div className="space-y-1">
                    {preview.differences.map((diff, diffIndex) => (
                      <div key={diffIndex} className="text-sm bg-gray-50 p-2 rounded dark:bg-gray-700">
                        <strong>{diff.field}:</strong> {diff.description}
                      </div>
                    ))}
                  </div>
                )}
                
                {preview.warnings.length > 0 && (
                  <div className="mt-2 text-sm text-yellow-600">
                    ⚠️ {preview.warnings.join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowDryRunModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setShowDryRunModal(false);
                handleBatchSync(false);
              }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
            >
              Execute Sync
            </button>
          </div>
        </div>
      </div>
    )
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading Stripe sync...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'sync', label: 'Sync Operations', icon: Upload },
            { key: 'batches', label: 'Batch History', icon: BarChart3 },
            { key: 'conflicts', label: 'Conflicts', icon: GitCompare },
            { key: 'reconciliation', label: 'Reconciliation', icon: Shield }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/10 dark:border-red-800">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'sync' && <SyncTab />}
      {activeTab === 'batches' && <BatchesTab />}
      {activeTab === 'conflicts' && (
        <div className="text-center py-12">
          <GitCompare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">No conflicts detected</p>
        </div>
      )}
      {activeTab === 'reconciliation' && <ReconciliationTab />}

      {/* Modals */}
      <DryRunModal />
    </div>
  );
};

export default EnhancedStripeSync;